import { env } from '@/lib/env';
import { auth } from '@/lib/firebase';
import { useEffect, useState } from 'react';

export type DocumentImageState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; url: string; contentType: string }
  | { status: 'not_found' }
  | { status: 'error'; message: string };

/**
 * Fetches a vehicle document as a blob via the gg-api authed content endpoint
 * and returns an object URL ready to use as <img src>.
 *
 * The object URL is revoked on unmount and whenever vehicleId / ref change,
 * preventing memory leaks.
 *
 * 404 → status "not_found" (friendly no-photo state, not an error crash).
 * Other non-2xx → status "error".
 * null/undefined ref → status "idle" (no fetch attempted).
 */
export function useDocumentImage(
  vehicleId: string | null | undefined,
  ref: string | null | undefined,
): DocumentImageState {
  const [state, setState] = useState<DocumentImageState>({ status: 'idle' });

  useEffect(() => {
    if (!vehicleId || !ref) {
      setState({ status: 'idle' });
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    setState({ status: 'loading' });

    async function fetchImage() {
      const user = auth.currentUser;
      if (!user) {
        if (!cancelled) setState({ status: 'error', message: 'Not signed in' });
        return;
      }

      const token = await user.getIdToken();
      const endpoint = `${env.VITE_GG_API_BASE_URL}/v1/vehicles/${vehicleId}/documents/${ref}/content`;

      let res: Response;
      try {
        res = await fetch(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        if (!cancelled) setState({ status: 'error', message: 'Network error fetching image' });
        return;
      }

      if (res.status === 404) {
        if (!cancelled) setState({ status: 'not_found' });
        return;
      }

      if (!res.ok) {
        if (!cancelled)
          setState({ status: 'error', message: `Could not load image (${res.status})` });
        return;
      }

      let blob: Blob;
      try {
        blob = await res.blob();
      } catch {
        if (!cancelled) setState({ status: 'error', message: 'Could not read image data' });
        return;
      }

      const contentType =
        res.headers.get('Content-Type') || blob.type || 'application/octet-stream';
      objectUrl = URL.createObjectURL(blob);
      if (!cancelled) {
        setState({ status: 'ready', url: objectUrl, contentType });
      } else {
        // Component unmounted while awaiting — revoke immediately.
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    }

    void fetchImage();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };
  }, [vehicleId, ref]);

  return state;
}
